/**
 * Закупки ТМЦ по месяцам (ADR-071 D10): позиция × месяц → сколько и на сколько.
 *
 * Правило месяца — ЗА МЕСЯЦ ДО начала работ, целиком, без размазывания:
 * семена лежат на складе к первому дню сева. Правило ВИДИМОЕ — печатаем его
 * текстом над таблицей (требование founder): снабженец, увидев «семена —
 * август», сразу понимает, что сев в сентябре.
 *
 * Деньги — только с правом на бюджетный справочник: без него количества
 * остаются, суммы честно скрыты (`prices_visible=false`).
 */
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatMoney, formatNumber } from '@/lib/planning/techCards';
import {
  hasUnscheduled,
  monthLabel,
  useProcurementReport,
  visibleMonths,
  type ProcurementCell,
  type ProcurementView,
  type ReportScope,
} from '@/lib/planning/techCardReports';

const VIEW_TITLES: Record<ProcurementView, string> = {
  qty: 'Объёмы',
  money: 'Деньги',
  both: 'Объёмы и деньги',
};

const SECTION_TITLES: Record<string, string> = {
  seed: 'Семена',
  fertilizer: 'Удобрения',
  pesticide: 'СЗР',
  other_tmc: 'Прочие ТМЦ',
  fuel: 'ГСМ',
};

export function ProcurementBlock({ scope }: { scope: ReportScope }) {
  const [view, setView] = useState<ProcurementView>('both');
  // Закупка — деньги календарного года (ADR-070 D3): семена осеннего сева
  // покупаются в этом году, хотя относятся на следующий урожай.
  const report = useProcurementReport(scope, 'cash', view);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-foreground">Закупки ТМЦ по месяцам</h3>
          <div className="ml-auto flex flex-wrap gap-2">
            {(Object.keys(VIEW_TITLES) as ProcurementView[]).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={view === key}
                onClick={() => setView(key)}
                className={
                  view === key
                    ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                    : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                }
              >
                {VIEW_TITLES[key]}
              </button>
            ))}
          </div>
        </div>

        {report.isLoading && <Skeleton className="mt-3 h-40 w-full" />}
        {report.isError && (
          <p className="mt-3 text-sm text-destructive">
            Не удалось посчитать закупки — обновите страницу.
          </p>
        )}

        {report.data && (
          <>
            <p className="mt-1 text-sm text-muted-foreground">
              Материалы и ГСМ из строк техкарт; {report.data.month_needed_rule_label}. Деньги
              календарного года сезона.
            </p>
            {!report.data.prices_visible && view !== 'qty' && (
              <p className="mt-1 text-xs text-muted-foreground">
                Суммы скрыты: плановые цены открыты только с доступом к бюджетному
                справочнику сезона.
              </p>
            )}
            <ProcurementTable data={report.data} view={view} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function cellText(cell: ProcurementCell | null | undefined, unit: string | null, view: ProcurementView): string {
  if (!cell) return '';
  const qty = cell.qty != null ? `${formatNumber(cell.qty)}${unit ? ` ${unit}` : ''}` : null;
  const amount = cell.amount != null ? formatMoney(cell.amount) : null;
  if (view === 'qty') return qty ?? '';
  if (view === 'money') return amount ?? '';
  if (qty && amount) return `${qty} · ${amount}`;
  return qty ?? amount ?? '';
}

function ProcurementTable({
  data,
  view,
}: {
  data: NonNullable<ReturnType<typeof useProcurementReport>['data']>;
  view: ProcurementView;
}) {
  if (data.rows.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        Закупать нечего: в строках техкарт нет материалов и своего топлива с посчитанным
        количеством.
      </p>
    );
  }
  const months = visibleMonths(data.rows);
  const withUnscheduled = hasUnscheduled(data.rows);

  return (
    <div className="mt-3 overflow-x-auto">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="text-xs">
            <TableHead>Позиция</TableHead>
            {months.map((month) => (
              <TableHead key={month} className="text-right whitespace-nowrap">
                {monthLabel(month)}
              </TableHead>
            ))}
            {withUnscheduled && <TableHead className="text-right">Без срока</TableHead>}
            <TableHead className="text-right">Всего</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rows.map((row) => (
            <TableRow key={`${row.budget_item_id}-${row.unit_id ?? 'u'}`}>
              <TableCell className="py-2">
                <div className="text-foreground">{row.name ?? 'позиция без названия'}</div>
                <div className="text-xs text-muted-foreground">
                  {SECTION_TITLES[row.section ?? ''] ?? 'ТМЦ'}
                </div>
              </TableCell>
              {months.map((month) => (
                <TableCell key={month} className="py-2 text-right whitespace-nowrap">
                  {cellText(row.months[month], row.unit, view)}
                </TableCell>
              ))}
              {withUnscheduled && (
                <TableCell
                  className="py-2 text-right whitespace-nowrap"
                  title="Строки без агроокна — месяц закупки не определить"
                >
                  {cellText(row.unscheduled, row.unit, view)}
                </TableCell>
              )}
              <TableCell className="py-2 text-right font-medium whitespace-nowrap">
                {cellText({ qty: row.total_qty, amount: row.total_amount }, row.unit, view)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {data.gaps.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-6 text-xs text-muted-foreground">
          {data.gaps.slice(0, 5).map((gap) => (
            <li key={gap}>{gap}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
